import {AfterViewChecked, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { moveItemInArray, CdkDropList, CdkDrag, CdkDragPlaceholder, CdkDragHandle } from '@angular/cdk/drag-drop';
import map from 'lodash/map';
import {EditPermissions} from '../../../../../../../../../shared/models';
import {required} from '../../../../../../../../../shared/utils';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { InputValidatorsComponent } from '../../../../templates/input-validators/input-validators.component';
import { MatTooltip } from '@angular/material/tooltip';

interface PickerPage {
  pageType: string;
  order?: number;
  pageHeader: string;
  pageSubHeader: string;
  pickerText: string;
  selectedItemIndex: number;
  options?: PickerPageOption[];
}

interface PickerPageOption {
  text: string;
  value: string;
  order?: number;
  identifier?: string;
}

@Component({
    selector: 'picker-page',
    templateUrl: './picker-page.component.html',
    styleUrls: ['../styles.scss', './picker-page.component.scss'],
    imports: [NgTemplateOutlet, NgClass, ReactiveFormsModule, InputValidatorsComponent, CdkDropList, CdkDrag, CdkDragPlaceholder, CdkDragHandle, MatTooltip]
})

export class PickerPageComponent implements OnInit, OnDestroy, AfterViewChecked {
  private activateScroll = false;
  constructor() {}

  @Input() pageData: any;
  @Input() canEdit: EditPermissions;
  @Output() setPageData = new EventEmitter();

  moreInfoOpened = false;

  pickerPageForm: UntypedFormGroup;
  pickerPageFormOptions: UntypedFormArray;

  ngOnInit() {
    this.pickerPageForm = this.getPickerPageTemplate();
    this.pickerPageFormOptions = this.getOptionsTemplate();

    const { page, newPage } = this.pageData;
    this.pickerPageForm.patchValue(page);
    if (page.options && page.options.length === 0) {
      this.addOption();
    }
    if (page.options && page.options.length > 0) {
      const options = this.pageData.page.options;
      for (const option in options) {
        this.addOption();
      }
      this.pickerPageFormOptions.patchValue(options);
    }

    if (newPage === false) {
      this.pickerPageForm.markAllAsTouched();
      this.pickerPageFormOptions.markAllAsTouched();
    }
    this.setPageDataHandler();
    this.listenForFormChanges();
  }

  ngOnDestroy() {
    this.setPageDataHandler();
  }

  ngAfterViewChecked(): void {
    this.scrollTo();
  }

  scrollTo() {
    if (this.activateScroll) {
      const elementID = `#option-${this.pickerPageFormOptions.length - 1}`;
      document.querySelector(elementID).scrollIntoView({behavior: 'smooth'});
      this.activateScroll = false;
    }
  }

  getPickerPageTemplate() {
    return new UntypedFormGroup({
      pageHeader: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }),
      pageSubHeader: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }, [required('Subheader')]),
      pickerText: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }),
      selectedItemIndex: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      })
    });
  }

  getOptionsTemplate() {
    return new UntypedFormArray([]);
  }

  getOptionTemplate() {
    return new UntypedFormGroup({
      text: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }, [required('Title')]),
      value: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }, [required('Value')]),
      identifier: new UntypedFormControl('')
    });
  }

  addOption() {
    this.pickerPageFormOptions.push(this.getOptionTemplate());
    this.activateScroll = true;
  }

  removeOption(index) {
    if (this.pickerPageFormOptions.controls.length > 1) {
      const options = this.pickerPageFormOptions as UntypedFormArray;
      options.removeAt(index);
    }
  }

  dropOption(event) {
    const currentOptionsList = this.pickerPageFormOptions.getRawValue();
    moveItemInArray(currentOptionsList, event.previousIndex, event.currentIndex);
    this.pickerPageFormOptions.patchValue(currentOptionsList);
    const pickerFormData = this.pickerPageForm.getRawValue();
    const { selectedItemIndex } = pickerFormData;
    this.updateSelectedItemIndex(selectedItemIndex, event);
  }

  private updateSelectedItemIndex(selectedItemIndex, event) {
    if (selectedItemIndex === event.previousIndex) {
      this.pickerPageForm.get('selectedItemIndex').setValue(event.currentIndex);
    }
    if (selectedItemIndex < event.previousIndex && selectedItemIndex >= event.currentIndex) {
      this.pickerPageForm.get('selectedItemIndex').setValue(this.pickerPageForm.get('selectedItemIndex').value + 1);
    }
    if (selectedItemIndex > event.previousIndex && selectedItemIndex <= event.currentIndex) {
      this.pickerPageForm.get('selectedItemIndex').setValue(this.pickerPageForm.get('selectedItemIndex').value - 1);
    }
  }

  setPageDataHandler() {
    const data = {
      ...this.pageData,
      page: this.getData()
    };
    this.setPageData.emit(data);
  }

  listenForFormChanges() {
    this.pickerPageForm.valueChanges.subscribe(values => {
      this.setPageDataHandler();
    });
    this.pickerPageFormOptions.valueChanges.subscribe(values => {
      this.setPageDataHandler();
    });
  }

  getData() {
    const pageFormValues = this.pickerPageForm.getRawValue();
    const options = this.pickerPageFormOptions.getRawValue();
    map(options, (option, index) => {
      option.order = index;
    });
    return {
      ...pageFormValues,
      pageType: this.pageData.type,
      valid: this.pickerPageForm.valid && this.pickerPageFormOptions.valid,
      options
    };
  }

  openMoreInfo() {
    this.moreInfoOpened = true;
  }

  closeMoreInfo() {
    this.moreInfoOpened = false;
  }
}
